import { StatusIndicator } from '@/components/StatusIndicator/StatusIndicator';
import type { TStatusIndicatorState } from '@/components/StatusIndicator/statusIndicator.types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Avatar,
  Badge,
  Dropdown,
  Icons,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  Tooltip,
  useLocalStorage,
} from '@commenda/kakashi';
import { OnboardingPill } from '@commenda/ui';
import { classNames, cn } from '@commenda/utils';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { CommendaLogo } from './CommendaLogo';

type OnboardingPillParams = {
  isActive: boolean;
  onClick: () => void;
};

declare global {
  interface Window {
    commendaNavbarPreferenceReady?: boolean;
  }
}

export type TSidebarSubItem = {
  key: string;
  label: string;
  href: string;
  isActive?: boolean;
};

export type TSidebarItem = {
  key: string;
  icon: (props: React.SVGProps<SVGSVGElement>) => React.JSX.Element;
  classNames?: {
    icon?: string;
    button?: string;
    label?: string;
    subItem?: string;
    activeSubItem?: string;
  };
  label: string;
  badge?: string;
  count?: number;
  showDot?: boolean;
  items: TSidebarSubItem[];
  isActive?: boolean;
  onClick?: () => void;
};

export type TSidebarGroup = {
  key: string;
  heading?: string;
  items: TSidebarItem[];
};

export interface TSidebarUser {
  name: string;
  image?: string | null;
  menuGroups: {
    items: {
      label: string;
      icon?: (props: React.SVGProps<SVGSVGElement>) => React.JSX.Element;
      shortcut?: React.ReactNode;
      onClick: () => void;
    }[];
  }[];
  menuClassName?: string;
}

export type TSidebarClassNames = {
  header?: string;
  footer?: string;
  userButton?: string;
};

export type TNavMode = 'home' | 'chat';

export type TSidebarRecentItem = {
  key: string;
  label: string;
  href: string;
  isActive?: boolean;
  status?: TStatusIndicatorState;
  count?: number;
  onDelete?: () => void;
  onRename?: (title: string) => void;
};

export function getNavItemClasses(isExpanded: boolean): string {
  return classNames(
    '!rounded-[6px] text-text-primary hover:!bg-bg-active-dark hover:!text-text-primary data-[active=true]:!bg-bg-active-dark data-[active=true]:!text-text-primary',
    'transition-colors duration-100 ease-[cubic-bezier(0.23,1,0.32,1)]',
    '!overflow-visible',
    isExpanded
      ? '!h-auto w-full !pl-2 !pr-lg !py-1.5 justify-start !gap-3'
      : '!size-7 !m-0 !p-0 !justify-center !items-center !gap-0',
    // Collapsed icon-mode overrides (must beat base group-data-[collapsible=icon]:!size-8)
    'group-data-[collapsible=icon]:!rounded-[6px] group-data-[collapsible=icon]:!bg-transparent group-data-[collapsible=icon]:hover:!bg-bg-active-dark data-[active=true]:group-data-[collapsible=icon]:!bg-bg-active-dark group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:!justify-center group-data-[collapsible=icon]:!gap-0 group-data-[collapsible=icon]:!items-center group-data-[collapsible=icon]:!h-7 group-data-[collapsible=icon]:!w-7 group-data-[collapsible=icon]:!m-0'
  );
}

export const MENU_ITEM_CLASSES = classNames(
  'w-full',
  'group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:w-7 group-data-[collapsible=icon]:h-7 group-data-[collapsible=icon]:mx-auto'
);

export function NavItem({
  item,
  isExpanded,
  openAccordion,
  onAccordionChange,
}: {
  item: TSidebarItem;
  isExpanded: boolean;
  openAccordion: string | undefined;
  onAccordionChange: (key: string | undefined) => void;
}) {
  const router = useRouter();

  const hasActiveSubItem = item.items.some((subItem) => subItem.isActive);
  const navItemClasses = getNavItemClasses(isExpanded);

  const iconClasses = classNames(
    '!size-4 shrink-0 transition-colors duration-100 ease-[cubic-bezier(0.23,1,0.32,1)]',
    item.isActive || hasActiveSubItem
      ? 'text-fg-brand'
      : 'text-fg-tertiary group-hover/menu-item:text-fg-tertiary-hover',
    item.classNames?.icon
  );

  const renderMenuButton = () => (
    <SidebarMenuButton
      tooltip={item.label}
      isActive={item.isActive || hasActiveSubItem}
      aria-current={item.isActive ? 'page' : undefined}
      className={cn(navItemClasses, item.classNames?.button)}
      {...(item.onClick ? { onClick: item.onClick } : {})}
    >
      <span
        className={classNames(
          'relative shrink-0 inline-flex items-center justify-center',
          !isExpanded && 'size-5'
        )}
      >
        <item.icon
          suppressHydrationWarning
          className={iconClasses}
          aria-hidden="true"
        />
        {item.showDot && (
          <span
            className={classNames(
              'absolute top-0 right-0 size-[6px] rounded-full bg-fg-brand ring-2',
              isExpanded && 'hidden',
              item.isActive || hasActiveSubItem
                ? 'ring-bg-active-dark'
                : 'ring-bg-light group-hover/menu-item:ring-bg-active-dark'
            )}
          />
        )}
      </span>
      <span
        className={classNames(
          'flex items-center justify-between flex-1',
          !isExpanded && 'hidden'
        )}
      >
        <span
          className={cn(
            'txt-extra-small-medium whitespace-nowrap overflow-hidden',
            item.classNames?.label
          )}
        >
          {item.label}
        </span>
        {item.showDot && item.count === undefined && (
          <span className="size-[6px] rounded-full bg-fg-brand shrink-0" />
        )}
        {item.badge && (
          <Badge variant="blue" size="sm">
            {item.badge}
          </Badge>
        )}
        {item.count !== undefined && (
          <span
            className={classNames(
              'font-mulish text-[11px] font-semibold leading-normal tracking-[-2%] shrink-0 transition-colors duration-100 ease-[cubic-bezier(0.23,1,0.32,1)]',
              item.isActive || hasActiveSubItem
                ? 'text-fg-brand'
                : 'text-fg-tertiary group-hover/menu-item:text-fg-tertiary-hover'
            )}
          >
            {item.count}
          </span>
        )}
      </span>
    </SidebarMenuButton>
  );

  const renderSubMenu = () => (
    <div className="flex flex-col p-1 gap-[2px] w-full">
      {item.items.map((subItem) => (
        <Link
          key={subItem.key}
          href={subItem.href}
          aria-current={subItem.isActive ? 'page' : undefined}
          className={cn(
            'flex items-center w-full py-sm pl-8 rounded-[6px] txt-extra-small-medium',
            'transition-colors duration-100 ease-[cubic-bezier(0.23,1,0.32,1)]',
            subItem.isActive
              ? 'bg-bg-primary text-text-primary'
              : 'bg-transparent text-text-tertiary hover:bg-bg-secondary hover:text-text-primary',
            item.classNames?.subItem,
            subItem.isActive && item.classNames?.activeSubItem
          )}
        >
          {subItem.label}
        </Link>
      ))}
    </div>
  );

  const shouldHighlightParent = hasActiveSubItem && openAccordion !== item.key;

  if (item.items.length === 0)
    return (
      <SidebarMenuItem className={MENU_ITEM_CLASSES}>
        {renderMenuButton()}
      </SidebarMenuItem>
    );

  if (!isExpanded) {
    return (
      <SidebarMenuItem className={MENU_ITEM_CLASSES}>
        <Dropdown
          side="right"
          sideOffset={8}
          align="start"
          menu={{
            groups: [
              {
                items: item.items.map((subItem) => ({
                  label: subItem.label,
                  className: subItem.isActive
                    ? 'bg-bg-tertiary text-text-primary'
                    : '',
                  onClick: () => {
                    router.push(subItem.href);
                  },
                })),
              },
            ],
          }}
        >
          {renderMenuButton()}
        </Dropdown>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem className={MENU_ITEM_CLASSES}>
      <Accordion
        type="single"
        collapsible
        className="w-full"
        value={openAccordion === item.key ? item.key : ''}
        onValueChange={(value) => {
          onAccordionChange(value || undefined);
        }}
      >
        <AccordionItem
          value={item.key}
          className="!border-b-0 !rounded-sm !bg-transparent data-[state='open']:!bg-bg-tertiary"
        >
          <SidebarMenuButton
            asChild
            isActive={item.isActive || shouldHighlightParent}
            className={classNames(
              navItemClasses,
              'data-[state=open]:!bg-transparent'
            )}
            onClick={(event) => {
              if (!hasActiveSubItem && item.items.length > 0) {
                const [firstSubItem] = item.items;
                if (firstSubItem?.href) {
                  event.preventDefault();
                  event.stopPropagation();
                  router.push(firstSubItem.href);
                }
              }
            }}
          >
            <AccordionTrigger
              className="w-full border-0 bg-transparent !px-0 !py-0"
              hideTriggerIcon
            >
              <div className="flex w-full items-center gap-3 px-2 py-1.5">
                <item.icon
                  suppressHydrationWarning
                  className={iconClasses}
                  aria-hidden="true"
                />
                {isExpanded ? (
                  <span className="flex items-center justify-between flex-1">
                    <span className="txt-extra-small-medium whitespace-nowrap overflow-hidden text-left">
                      {item.label}
                    </span>
                    {item.badge && (
                      <Badge variant="blue" size="sm">
                        {item.badge}
                      </Badge>
                    )}
                  </span>
                ) : null}
              </div>
            </AccordionTrigger>
          </SidebarMenuButton>
          <AccordionContent className="pb-0">
            {renderSubMenu()}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </SidebarMenuItem>
  );
}

const NAV_MODES: {
  key: TNavMode;
  label: string;
  icon: (props: React.SVGProps<SVGSVGElement>) => React.JSX.Element;
}[] = [
  { key: 'home', label: 'Home', icon: Icons.HomeLine },
  { key: 'chat', label: 'Chat', icon: Icons.Pneuma },
];

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

function NavModeSwitcher({
  mode,
  onModeChange,
  isExpanded,
}: {
  mode: TNavMode;
  onModeChange: (mode: TNavMode) => void;
  isExpanded: boolean;
}) {
  return (
    <div
      className={classNames(
        'flex',
        isExpanded
          ? 'flex-row items-center gap-xs'
          : 'flex-col items-center gap-sm'
      )}
    >
      {NAV_MODES.map((item) => {
        const isActive = mode === item.key;
        return (
          <Tooltip
            key={item.key}
            content={item.label}
            side="top"
            sideOffset={11}
            asChild
          >
            <button
              type="button"
              aria-label={item.label}
              aria-pressed={isActive}
              onClick={() => onModeChange(item.key)}
              className={classNames(
                'inline-flex items-center justify-center gap-md cursor-pointer border-none',
                'transition-[color,background-color,transform] duration-100 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.94]',
                // Expanded: segmented-control pill. Collapsed rail: match the
                // active nav-item icon slot (rounded square + brand icon).
                isExpanded &&
                  isActive &&
                  'h-7 px-lg rounded-full bg-bg-tertiary',
                isExpanded &&
                  !isActive &&
                  'p-sm rounded-full bg-transparent hover:bg-bg-active-dark',
                !isExpanded && 'size-7 rounded-[6px]',
                !isExpanded && isActive && 'bg-bg-active-dark',
                !isExpanded &&
                  !isActive &&
                  'bg-transparent hover:bg-bg-active-dark',
                isActive && isExpanded && 'text-text-primary',
                isActive && !isExpanded && 'text-fg-brand',
                !isActive && 'text-fg-tertiary hover:text-text-primary'
              )}
            >
              <item.icon className="!size-4 shrink-0" aria-hidden="true" />
              {isExpanded && isActive && (
                <span className="txt-extra-small-medium whitespace-nowrap">
                  {item.label}
                </span>
              )}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}

function RecentItem({
  item,
  isExpanded,
  index = 0,
  reduceMotion = false,
}: {
  item: TSidebarRecentItem;
  isExpanded: boolean;
  index?: number;
  reduceMotion?: boolean;
}) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState(item.label);
  const inputRef = useRef<HTMLInputElement>(null);
  const renamingRef = useRef(false);

  useEffect(() => {
    if (isRenaming) inputRef.current?.select();
  }, [isRenaming]);

  const startRename = () => {
    renamingRef.current = true;
    setDraft(item.label);
    setIsRenaming(true);
  };

  const finishRename = (save: boolean) => {
    if (!renamingRef.current) return;
    renamingRef.current = false;
    setIsRenaming(false);
    const next = draft.trim();
    if (save && next && next !== item.label) item.onRename?.(next);
    else setDraft(item.label);
  };

  if (!isExpanded)
    return (
      <Tooltip content={item.label} side="right" sideOffset={8} asChild>
        <Link
          href={item.href}
          className={classNames(
            'flex size-7 items-center justify-center mx-auto rounded-[6px]',
            'transition-colors duration-100 ease-[cubic-bezier(0.23,1,0.32,1)]',
            item.isActive
              ? 'bg-bg-active-dark text-text-primary'
              : 'text-text-secondary hover:bg-bg-active-dark hover:text-text-primary'
          )}
        >
          <Icons.MessageCircle01
            className="!size-4 shrink-0 text-fg-tertiary"
            aria-hidden="true"
          />
        </Link>
      </Tooltip>
    );

  return (
    <motion.div
      layout={reduceMotion ? false : 'position'}
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.18,
          ease: EASE_OUT,
          delay: reduceMotion ? 0 : Math.min(index * 0.03, 0.15),
        },
      }}
      exit={
        reduceMotion
          ? { opacity: 0, transition: { duration: 0.12 } }
          : {
              opacity: 0,
              scale: 0.96,
              transition: { duration: 0.14, ease: EASE_OUT },
            }
      }
      className={classNames(
        'group/recent flex h-8 items-center rounded-[6px] pr-1',
        'transition-colors duration-100 ease-[cubic-bezier(0.23,1,0.32,1)]',
        item.isActive ? 'bg-bg-active-dark' : 'hover:bg-bg-active-dark'
      )}
    >
      {isRenaming ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              finishRename(true);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              finishRename(false);
            }
          }}
          onBlur={() => finishRename(true)}
          aria-label="Rename conversation"
          className="txt-extra-small-medium w-full min-w-0 flex-1 rounded-[6px] border-none bg-transparent px-md py-sm text-text-primary outline-none"
        />
      ) : (
        <>
          <Link
            href={item.href}
            className={classNames(
              'flex h-full items-center gap-sm px-md flex-1 min-w-0',
              item.isActive
                ? 'text-text-primary'
                : 'text-text-secondary group-hover/recent:text-text-primary'
            )}
          >
            <StatusIndicator state={item.status ?? 'idle'} />
            <span className="txt-extra-small-medium min-w-0 flex-1 truncate">
              {item.label}
            </span>
            {item.count !== undefined && item.count > 0 && (
              <span
                className="flex shrink-0 items-center rounded-[4px] bg-bg-tertiary px-[6px] py-[2px] txt-extra-small-medium text-text-tertiary"
                aria-label={`${item.count} new runs`}
              >
                {item.count > 99 ? '99+' : item.count} new
              </span>
            )}
          </Link>
          {(item.onRename || item.onDelete) && (
            <Dropdown
              align="start"
              side="bottom"
              menu={{
                groups: [
                  {
                    items: [
                      ...(item.onRename
                        ? [
                            {
                              label: 'Rename',
                              icon: Icons.Edit05,
                              onClick: startRename,
                            },
                          ]
                        : []),
                      ...(item.onDelete
                        ? [
                            {
                              label: 'Delete',
                              icon: Icons.Trash01,
                              className:
                                'text-text-error hover:text-text-error focus:text-text-error',
                              onClick: item.onDelete,
                            },
                          ]
                        : []),
                    ],
                  },
                ],
              }}
            >
              <button
                type="button"
                aria-label="Conversation options"
                className={classNames(
                  // Collapsed to w-0 until the row is hovered/focused (or the menu is open) so the title
                  // gets the full width and only truncates to make room once the button actually appears.
                  'inline-flex h-6 w-0 shrink-0 items-center justify-center cursor-pointer overflow-hidden rounded-[6px] border-none bg-transparent',
                  'text-fg-quaternary hover:bg-bg-tertiary hover:text-text-primary',
                  // Reveal is instant (no opacity/width transition); only the press scale animates.
                  'opacity-0 transition-transform duration-100 active:scale-90',
                  'group-hover/recent:w-6 group-hover/recent:opacity-100 focus:w-6 focus:opacity-100 data-[state=open]:w-6 data-[state=open]:opacity-100'
                )}
              >
                <Icons.DotsHorizontal className="size-3.5" aria-hidden="true" />
              </button>
            </Dropdown>
          )}
        </>
      )}
    </motion.div>
  );
}

function RecentsSection({
  heading,
  items,
  isExpanded,
  defaultOpen,
  emptyText,
}: {
  heading: string;
  items: TSidebarRecentItem[];
  isExpanded: boolean;
  defaultOpen: boolean;
  emptyText: string | null;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const reduceMotion = useReducedMotion() ?? false;

  // Hidden entirely on the collapsed rail — the recents list is meaningless without its labels.
  if (!isExpanded) return null;

  return (
    <div className="flex flex-col gap-md w-full">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex items-center gap-xs pl-md bg-transparent border-none cursor-pointer"
      >
        <span className="txt-extra-small-medium text-text-quaternary">
          {heading}
        </span>
        <Icons.ChevronRight
          className={classNames(
            'size-3 text-fg-quaternary transition-transform duration-100 ease-[cubic-bezier(0.23,1,0.32,1)]',
            open && 'rotate-90'
          )}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="flex flex-col !gap-0.5 w-full">
          <AnimatePresence mode="popLayout">
            {items.map((item, index) => (
              <RecentItem
                key={item.key}
                item={item}
                isExpanded
                index={index}
                reduceMotion={reduceMotion}
              />
            ))}
          </AnimatePresence>
          {items.length === 0 && emptyText !== null && (
            <span className="txt-extra-small-medium text-text-quaternary px-md py-sm">
              {emptyText}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** One sidebar group. When expanded and the group has a heading, the heading is a collapsible
 * toggle (chevron + click-to-hide-items) mirroring RecentsSection; headingless groups and the
 * collapsed rail always show their items. */
function NavGroup({
  group,
  isExpanded,
  openAccordion,
  onAccordionChange,
}: {
  group: TSidebarGroup;
  isExpanded: boolean;
  openAccordion: string | undefined;
  onAccordionChange: (key: string | undefined) => void;
}) {
  const [open, setOpen] = useState(true);
  const isCollapsible = isExpanded && Boolean(group.heading);

  return (
    <SidebarGroup className="flex flex-col gap-md !p-0">
      {isCollapsible && (
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          className="flex items-center gap-xs pl-md bg-transparent border-none cursor-pointer"
        >
          <span className="txt-extra-small-medium text-text-quaternary">
            {group.heading}
          </span>
          <Icons.ChevronRight
            className={classNames(
              'size-3 text-fg-quaternary transition-transform duration-100 ease-[cubic-bezier(0.23,1,0.32,1)]',
              open && 'rotate-90'
            )}
            aria-hidden="true"
          />
        </button>
      )}
      {(open || !isCollapsible) && (
        <SidebarMenu className="flex flex-col items-start !gap-0.5 w-full !px-0">
          {group.items.map((item) => (
            <NavItem
              key={item.key}
              item={item}
              isExpanded={isExpanded}
              openAccordion={openAccordion}
              onAccordionChange={onAccordionChange}
            />
          ))}
        </SidebarMenu>
      )}
    </SidebarGroup>
  );
}

export const CommendaNavbar = ({
  groups,
  mode,
  onModeChange,
  recents,
  chatNavItems,
  agentSessions,
  user,
  onboardingPill,
  footerGroups,
  classNames: navbarClassNames,
  children,
}: {
  groups: TSidebarGroup[];
  /** Rows pinned above the user block, outside the scrolling content. */
  footerGroups?: TSidebarGroup[];
  classNames?: TSidebarClassNames;
  mode?: TNavMode;
  onModeChange?: (mode: TNavMode) => void;
  /** A host that reads its recents passes the read's state, so a load or a failure never reads as empty. */
  recents?: {
    items: TSidebarRecentItem[];
    isLoading?: boolean;
    error?: unknown;
  };
  chatNavItems?: TSidebarItem[];
  agentSessions?: { items: TSidebarRecentItem[] };
  user?: TSidebarUser;
  onboardingPill?: OnboardingPillParams;
  children: React.ReactNode;
}) => {
  const shouldInitializeFromStorage =
    typeof window !== 'undefined'
      ? (window.commendaNavbarPreferenceReady ?? false)
      : false;

  const [isExpanded, setExpanded] = useLocalStorage(
    'is-sidebar-expanded',
    true,
    {
      initializeWithValue: shouldInitializeFromStorage,
    }
  );

  const [isInitialStateResolved, setIsInitialStateResolved] = useState(
    shouldInitializeFromStorage
  );

  useEffect(() => {
    if (shouldInitializeFromStorage) {
      return undefined;
    }

    const resolveInitialState = () => {
      try {
        const storedValue = window.localStorage.getItem('is-sidebar-expanded');
        if (storedValue !== null) {
          const parsed = JSON.parse(storedValue);
          if (typeof parsed === 'boolean') {
            setExpanded(parsed);
          }
        }
      } catch (error) {
        console.error(
          'Failed to read sidebar preference from localStorage',
          error
        );
      } finally {
        setIsInitialStateResolved(true);
      }
    };

    resolveInitialState();

    return undefined;
  }, [shouldInitializeFromStorage, setExpanded]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.commendaNavbarPreferenceReady = true;

      if (!isInitialStateResolved) {
        setIsInitialStateResolved(true);
      }
    }
  }, [isInitialStateResolved]);

  const [openAccordion, setOpenAccordion] = useState<string | undefined>(() => {
    const activeItem = groups
      .flatMap((group) => group.items)
      .find((item) => item.isActive || item.items.some((sub) => sub.isActive));
    return activeItem?.key;
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(false);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight;
    const atTop = el.scrollTop <= 0;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    setShowTopGradient(hasOverflow && !atTop);
    setShowBottomGradient(hasOverflow && !atBottom);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', handleScroll, { passive: true });
    // Re-check on container/content size changes (data loads, sidebar expand/collapse, viewport resize)
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(el);
    Array.from(el.children).forEach((child) => resizeObserver.observe(child));
    // Check initial state
    handleScroll();
    return () => {
      el.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [handleScroll, isExpanded, groups]);

  return (
    <SidebarProvider
      open={isExpanded}
      onOpenChange={setExpanded}
      style={
        {
          '--sidebar-width': '208px',
          '--sidebar-width-icon': '56px',
        } as React.CSSProperties
      }
    >
      <Sidebar
        collapsible="icon"
        className={classNames(
          'h-screen !border-r-0',
          !isInitialStateResolved && 'opacity-0 transition-none'
        )}
      >
        <SidebarHeader
          className={cn(
            'px-lg pt-lg pb-none bg-bg-light flex flex-col gap-3xl',
            navbarClassNames?.header
          )}
        >
          <div
            className={classNames(
              'flex',
              isExpanded
                ? 'flex-row items-center justify-between'
                : 'flex-col items-center gap-lg'
            )}
          >
            <CommendaLogo collapsed={!isExpanded} />
            <Tooltip
              content={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              side="right"
              sideOffset={11}
              asChild
            >
              <SidebarTrigger asChild>
                <button
                  type="button"
                  aria-label={
                    isExpanded ? 'Collapse sidebar' : 'Expand sidebar'
                  }
                  className="inline-flex items-center justify-center cursor-pointer bg-transparent border-none p-sm rounded-md text-fg-quaternary hover:bg-bg-active-dark hover:text-text-primary transition-colors duration-100 ease-[cubic-bezier(0.23,1,0.32,1)]"
                >
                  <Icons.LayoutRight className="size-4 rotate-180" />
                </button>
              </SidebarTrigger>
            </Tooltip>
          </div>
          {mode && onModeChange && (
            <NavModeSwitcher
              mode={mode}
              onModeChange={onModeChange}
              isExpanded={isExpanded}
            />
          )}
        </SidebarHeader>

        <SidebarContent className="bg-bg-light px-lg flex flex-col overflow-hidden relative">
          <div
            ref={scrollRef}
            className="flex flex-col gap-3xl flex-1 min-h-0 overflow-y-auto -mx-lg px-lg pt-3xl transition-[margin] duration-100 ease-out"
          >
            {mode === 'chat' ? (
              <>
                {chatNavItems && chatNavItems.length > 0 && (
                  <SidebarMenu className="flex flex-col !gap-0.5">
                    {chatNavItems.map((item) => (
                      <NavItem
                        key={item.key}
                        item={item}
                        isExpanded={isExpanded}
                        openAccordion={openAccordion}
                        onAccordionChange={setOpenAccordion}
                      />
                    ))}
                  </SidebarMenu>
                )}
                {agentSessions && agentSessions.items.length > 0 && (
                  <RecentsSection
                    heading="Scheduled"
                    items={agentSessions.items}
                    isExpanded={isExpanded}
                    defaultOpen
                    emptyText={null}
                  />
                )}
                {recents && (
                  <RecentsSection
                    heading="Recents"
                    items={recents.items}
                    isExpanded={isExpanded}
                    defaultOpen
                    emptyText={
                      recents.isLoading
                        ? 'Loading…'
                        : recents.error
                          ? "Couldn't load conversations."
                          : 'No conversations yet.'
                    }
                  />
                )}
              </>
            ) : (
              <>
                {onboardingPill && (
                  <OnboardingPill {...onboardingPill} isExpanded={isExpanded} />
                )}
                {groups.map((group) => (
                  <NavGroup
                    key={group.key}
                    group={group}
                    isExpanded={isExpanded}
                    openAccordion={openAccordion}
                    onAccordionChange={setOpenAccordion}
                  />
                ))}
              </>
            )}
          </div>
          {showTopGradient && (
            <div className="pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-bg-light to-transparent" />
          )}
          {showBottomGradient && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-bg-light to-transparent" />
          )}
        </SidebarContent>

        <SidebarFooter
          className={cn(
            'px-lg pb-md pt-2 bg-bg-light',
            navbarClassNames?.footer
          )}
        >
          {footerGroups?.map((group) => (
            <NavGroup
              key={group.key}
              group={group}
              isExpanded={isExpanded}
              openAccordion={openAccordion}
              onAccordionChange={setOpenAccordion}
            />
          ))}
          <SidebarMenu className="flex flex-col !gap-0.5">
            {user && (
              <SidebarMenuItem>
                <Dropdown
                  side="right"
                  sideOffset={8}
                  align="end"
                  menu={{ groups: user.menuGroups }}
                  contentClassName={user.menuClassName}
                >
                  <SidebarMenuButton
                    tooltip={user.name}
                    className={cn(
                      '!rounded-[6px] text-text-primary hover:!bg-bg-active-dark hover:!text-text-primary',
                      isExpanded
                        ? '!h-auto w-full !px-1 !py-1.5 justify-start !gap-2'
                        : '!size-7 !m-0 !p-0 !justify-center !items-center !gap-0',
                      'group-data-[collapsible=icon]:!rounded-[6px] group-data-[collapsible=icon]:!bg-transparent group-data-[collapsible=icon]:hover:!bg-bg-active-dark group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:!justify-center group-data-[collapsible=icon]:!gap-0 group-data-[collapsible=icon]:!items-center group-data-[collapsible=icon]:!h-7 group-data-[collapsible=icon]:!w-7 group-data-[collapsible=icon]:!m-0',
                      isExpanded && navbarClassNames?.userButton
                    )}
                  >
                    <Avatar
                      size="xs"
                      name={user.name}
                      image={user.image}
                      className="shrink-0 !border-[0.5px] rounded-full shadow-[0_2px_8px_0_rgba(0,0,0,0.04)] transition-shadow duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover/menu-item:shadow-[0_2px_12px_0_rgba(0,0,0,0.08)]"
                    />
                    {isExpanded && (
                      <span className="flex items-center justify-between flex-1 min-w-0">
                        <span className="txt-extra-small-medium text-text-primary truncate">
                          {user.name}
                        </span>
                        <Icons.ChevronRight className="size-4 text-fg-quaternary shrink-0" />
                      </span>
                    )}
                  </SidebarMenuButton>
                </Dropdown>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset className="!w-auto min-w-0 overflow-hidden">
        {children}
      </SidebarInset>
    </SidebarProvider>
  );
};
